import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import type { Lead } from '../../../entities/lead/model/types';
import { pb } from '../../../shared/api/pocketbase';

interface AnalyticsCardsModalProps {
  title: string;
  ids: string[];
  onClose: () => void;
}

const PAGE = 20;

const AnalyticsCardsModal: React.FC<AnalyticsCardsModalProps> = ({ title, ids, onClose }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(0);

  const loadMore = useCallback(async () => {
    if (loading || loadedRef.current >= ids.length) return;
    const slice = ids.slice(loadedRef.current, loadedRef.current + PAGE);
    setLoading(true);
    try {
      const batch = await pb.collection('tasks').getFullList<Lead>({
        filter: slice.map(id => `id="${id}"`).join(' || '),
        fields: 'id,title,num,phone,city',
        requestKey: null,
      });
      // сохраняем порядок как в ids
      const byId = new Map(batch.map(l => [l.id, l]));
      const ordered = slice.map(id => byId.get(id)).filter(Boolean) as Lead[];
      loadedRef.current += slice.length;
      setLeads(prev => [...prev, ...ordered]);
    } catch { /* keep what loaded */ }
    finally { setLoading(false); }
  }, [ids, loading]);

  // первая порция при открытии
  useEffect(() => {
    loadedRef.current = 0;
    setLeads([]);
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) void loadMore();
  };

  return (
    <div className="mo open" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cm cm-create" style={{ maxWidth: 480 }}>
        <div className="cm-top" style={{ borderRadius: '20px 20px 0 0' }}>
          <div className="cm-ti">{title} · {ids.length}</div>
          <div className="cm-sp" />
          <button className="cm-x" onClick={onClose} type="button">
            <IconX size={16} stroke={2} />
          </button>
        </div>
        <div
          className="cm-create-body"
          style={{ gap: 8, borderRadius: '0 0 20px 20px', maxHeight: '60vh', overflowY: 'auto' }}
          onScroll={onScroll}
        >
          {leads.length === 0 && !loading ? (
            <div className="adm-empty">Нет карточек</div>
          ) : (
            leads.map(l => (
              <div key={l.id} className="af-card-row">
                <span className="af-card-row-title">
                  {l.num != null ? `#${l.num} ` : ''}{l.title || 'Без названия'}
                </span>
                <span className="af-card-row-sub">
                  {[l.city, l.phone].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))
          )}
          {loading && <div className="adm-loading">Загрузка...</div>}
        </div>
      </div>
    </div>
  );
};

export { AnalyticsCardsModal };
